<script setup lang="ts">
import { computed, onMounted, ref, watch, watchEffect } from 'vue'
import {
    AccountNavigationStore,
    navigationItemsPersonal,
    PersonalAccountPage,
    useAccountNavigationState,
} from '../../../utilities/account/useAccountNavigationPersonal'
import AccountPanelPersonalPersonalData from './pages/AccountPanelPersonalPersonalData.vue'
import AccountPanelPersonalAddressData from './pages/AccountPanelPersonalAddressData.vue'
import AccountPanelPersonalContactData from './pages/AccountPanelPersonalContactData.vue'
import AccountPanelPersonalLegalInformation from './pages/AccountPanelPersonalLegalInformation.vue'
import AccountPanelPersonalVerification from './pages/AccountPanelPersonalVerification.vue'
import AccountPanelPersonalBankData from './pages/AccountPanelPersonalBankData.vue'
import AccountPanelPersonalTaxData from './pages/AccountPanelPersonalTaxData.vue'
import AccountPanelPersonalCryptoWallet from './pages/AccountPanelPersonalCryptoWallet.vue'
import AccountPanelPersonalSignatures from './pages/AccountPanelPersonalSignatures.vue'

import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { i18n } from '../../../localization'
import {
    PersonalAccountFormStore,
    usePersonalAccountFormState,
} from '../../../utilities/account/useAccountFormPersonal'
import AccountPanelMissingDataAlert from './AccountPanelPersonalMissingDataAlert.vue'
import AccountPanelMissingVerificationAlert from './AccountPanelPersonalMissingVerificationAlert.vue'
import AccountPanel, { RouterItem } from '../AccountPanel.vue'
import AccountPersonalDashboard from './AccountPersonalDashboard.vue'
import AccountPanelReferralProgram from './pages/AccountPanelReferralProgram.vue'
import AccountPanelPersonalPendingInvestmentProcessAlert from './AccountPanelPersonalPendingInvestmentProcessAlert.vue'
import AccountPanelPersonalMissingSignaturesAlert from './AccountPanelPersonalMissingSignaturesAlert.vue'
import KycVerificationStatus from '../common/verification/KycVerificationStatus.vue'
import { getPlatformSettings } from '../../../utilities/usePlatformSettings'
import AccountPanelPersonalComplianceProfile from './pages/AccountPanelPersonalComplianceProfile.vue'
import AccountPanelPersonalSecurity from './pages/AccountPanelPersonalSecurity.vue'
import TinForm from '../../wizard/ESAC/form/TinForm.vue'
import Alert from '../../base/Alert.vue'
import { ExclamationCircleIcon, UserCircleIcon } from '@heroicons/vue/solid'
import { isEsacPersonalComplianceProfileLocked } from './complianceProfile/esacComplianceProfile'
import AccountPanelPersonalNyalaVault from './pages/AccountPanelPersonalNyalaVault.vue'
import MembershipManagementPanel from '../membershipManagement/MembershipManagementPanel.vue'

const navigationStore: AccountNavigationStore = useAccountNavigationState() as AccountNavigationStore
const store = usePersonalAccountDataState()
const accountFormStore = usePersonalAccountFormState() as PersonalAccountFormStore
const condaUseV2FrontendPortfolioDashboard = ref<number>(0)

watchEffect(() => {
    condaUseV2FrontendPortfolioDashboard.value = window.condaUseV2FrontendPortfolioDashboard
})
const router: { [key: PersonalAccountPage]: RouterItem } = {
    'Dashboard': {
        title: i18n.global.t('account_settings.dashboard'),
        component: AccountPersonalDashboard,
        customPanel: computed(() => !!condaUseV2FrontendPortfolioDashboard.value),
    },
    'Personal Data': {
        title: i18n.global.t('account_settings.personal_data_headline'),
        component: AccountPanelPersonalPersonalData,
    },
    'Address Data': {
        title: i18n.global.t('account_settings.address_data'),
        subtitle: i18n.global.t('account_settings.address_data_subtitle'),
        component: AccountPanelPersonalAddressData,
    },
    'Contact Data': {
        title: i18n.global.t('account_settings.contact_data_headline'),
        component: AccountPanelPersonalContactData,
    },
    'Verification': {
        title: i18n.global.t('account_settings.verification_headline'),
        subtitle: i18n.global.t('account_settings.verification_subtitle'),
        component: AccountPanelPersonalVerification,
        hideFooter: computed(() => true),
        hideSubtitle: computed(() => store.isVerified),
    },
    'Bank Data': {
        title: i18n.global.t('account_settings.bank_data_headline'),
        subtitle: i18n.global.t('account_settings.bank_data_subtitle'),
        component: AccountPanelPersonalBankData,
        hideFooter: computed(() => true),
    },
    'Legal Information': {
        title: i18n.global.t('account_settings.legal_information'),
        subtitle: i18n.global.t('account_settings.legal_information_subtitle'),
        component: AccountPanelPersonalLegalInformation,
    },
    'Compliance Profile': {
        title: i18n.global.t('account_settings.compliance_profile'),
        subtitle: i18n.global.t('account_settings.compliance_profile_subtitle'),
        component: AccountPanelPersonalComplianceProfile,
        hideFooter: computed(() => true),
        hideHeader: computed(() => usePersonalAccountFormState().currentForm !== null),
    },
    'Security': {
        title: i18n.global.t('account_settings.security_headline'),
        subtitle: i18n.global.t('account_settings.security_subtitle'),
        component: AccountPanelPersonalSecurity,
        hideFooter: computed(() => true),
    },
    'Tax Data': {
        title: i18n.global.t('account_settings.tax_data_headline'),
        component: AccountPanelPersonalTaxData,
    },
    'Tax Residencies': {
        title: i18n.global.t('account_settings.tax_residencies'),
        component: TinForm,
    },
    'Crypto Wallet': {
        title: i18n.global.t('account_settings.crypto_wallet_headline'),
        component: AccountPanelPersonalCryptoWallet,
        hideFooter: computed(() => true),
    },
    'Nyala Vault': {
        title: i18n.global.t('account_settings.nyala_vault_headline'),
        subtitle: i18n.global.t('account_settings.nyala_vault_subtitle'),
        component: AccountPanelPersonalNyalaVault,
        hideFooter: computed(() => true),
    },
    'Referral': {
        title: i18n.global.t('account_settings.referral_program'),
        subtitle: i18n.global.t('account_settings.referral_program_subtitle'),
        component: AccountPanelReferralProgram,
        hideFooter: computed(() => true),
    },
    'Signatures': {
        title: i18n.global.t('account_settings.signatures_headline'),
        subtitle: i18n.global.t('account_settings.signatures_subtitle'),
        component: AccountPanelPersonalSignatures,
        hideFooter: computed(() => true),
    },
    'Membership Management': {
        title: i18n.global.t('account_settings.membership_management'),
        component: MembershipManagementPanel,
        hideFooter: computed(() => true),
    },
}

const showDataMissingAlert = ref(false)

const showMissingSignaturesAlert = computed(() => {
    return (
        store.naturalProfile.signatures.length > 0 &&
        store.naturalProfile.signatures.filter((signature) => signature.signed_at === null).length > 0
    )
})

onMounted(() => {
    showDataMissingAlert.value = !store.hasAllRequiredNaturalProfileAttributesForInvestment
})

watch(
    () => store.saving,
    () => {
        if (!store.hasAllRequiredNaturalProfileAttributesForInvestment) {
            showDataMissingAlert.value = true
        }
    }
)
</script>

<template>
    <account-panel
        :store="store"
        :router="router"
        :navigation-store="navigationStore"
        :account-form-store="accountFormStore"
        :navigation-items="navigationItemsPersonal">
        <template #alerts>
            <div class="mb-4 space-y-4">
                <AccountPanelPersonalPendingInvestmentProcessAlert></AccountPanelPersonalPendingInvestmentProcessAlert>
                <AccountPanelMissingDataAlert v-if="showDataMissingAlert"></AccountPanelMissingDataAlert>
                <AccountPanelMissingVerificationAlert v-if="!store.isVerified"></AccountPanelMissingVerificationAlert>
                <AccountPanelPersonalMissingSignaturesAlert
                    v-if="showMissingSignaturesAlert"></AccountPanelPersonalMissingSignaturesAlert>
                <Alert
                    v-if="
                        getPlatformSettings().compliance_profile === 'esac' && isEsacPersonalComplianceProfileLocked()
                    "
                    :title-text="$t('account.esac_locked_account_info')">
                    <template #icon>
                        <UserCircleIcon class="h-12 w-12 text-gray-300"></UserCircleIcon>
                        <ExclamationCircleIcon
                            class="absolute bottom-0 right-0 h-6 w-6 text-yellow-400"></ExclamationCircleIcon>
                    </template>
                </Alert>

                <div
                    v-if="showDataMissingAlert || !store.isVerified || showMissingSignaturesAlert"
                    class="border-b border-gray-300 pt-2"></div>
            </div>
        </template>
        <template #header-right>
            <KycVerificationStatus v-if="navigationStore.page === 'Verification'" :is-verified="store.isVerified" />
        </template>
    </account-panel>
</template>
